"use client"

import { useCallback, useRef, useState } from "react"
import { useMeetingRecordingStore } from "@/stores/use-meeting-recording-store"
import { DndContext, PointerSensor, useDraggable, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical } from "lucide-react"

import { useMeeting } from "@/hooks/api/meetings"
import { Link } from "@/components/book-link"
import { ProcessingStatusView } from "@/components/meetings/processing-status-view"
import { RecordingVisualizer } from "@/components/meetings/recording-visualizer"

export function RecordingContainer() {
  const { isVisible, isRecording, meetingId, meetingTitle, processingMeetingId } = useMeetingRecordingStore()
  const { data: recordingMeeting } = useMeeting(meetingId || "", { enabled: !!meetingId && isRecording })

  const containerRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  )

  const onDragEnd = useCallback((event: DragEndEvent) => {
    setPosition((prev) => {
      const newX = prev.x + event.delta.x
      const newY = prev.y + event.delta.y

      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return { x: newX, y: newY }

      const cssOffsetTop = 32
      const cssOffsetRight = 32
      const edgePadding = 8

      const minX = -(window.innerWidth - rect.width - cssOffsetRight - edgePadding)
      const maxX = -edgePadding + cssOffsetRight
      const minY = edgePadding - cssOffsetTop
      const maxY = window.innerHeight - rect.height - cssOffsetTop - edgePadding

      return {
        x: Math.max(minX, Math.min(newX, maxX)),
        y: Math.max(minY, Math.min(newY, maxY)),
      }
    })
  }, [])

  const showRecording = isVisible && isRecording
  const showProcessing = !!processingMeetingId

  if (!showRecording && !showProcessing) return null

  const displayTitle = meetingTitle || recordingMeeting?.title || "Untitled meeting"
  const displayMeetingId = meetingId || processingMeetingId

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <DraggableFloating
        id="recording-container"
        position={position}
        className="fixed top-8 right-8 z-50 w-[388px] max-w-[calc(100vw-4rem)]"
        containerRef={containerRef}
      >
        <div className="bg-primary overflow-hidden rounded-md border shadow-lg">
          {showRecording && (
            <>
              {displayMeetingId && (
                <div className="flex items-center justify-between p-4 pb-0">
                  <Link href={`/meetings/${displayMeetingId}`} className="min-w-0">
                    <span className="text-background block truncate text-base underline decoration-dotted underline-offset-2">
                      {displayTitle}
                    </span>
                  </Link>
                  <GripVertical className="text-icon-muted hover:text-icon size-4 flex-none" />
                </div>
              )}
              <RecordingVisualizer />
            </>
          )}
          {!showRecording && showProcessing && <ProcessingStatusView />}
        </div>
      </DraggableFloating>
    </DndContext>
  )
}

/**
 * DraggableFloating: turns its children into a free-floating, draggable container.
 * - Uses CSS translate for buttery-smooth dragging.
 * - Keeps clicks working (activation distance = 6px).
 * - Cursor changes to indicate dragging.
 * - Constrained to viewport bounds.
 */
function DraggableFloating(props: {
  id: string
  position: { x: number; y: number }
  className?: string
  children: React.ReactNode
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  const { id, position, className, children, containerRef } = props
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id })

  // Track pointer movement to suppress a click after a drag gesture
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null)
  const suppressClickRef = useRef<boolean>(false)

  // Combine the persistent position with the live drag transform
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString({
      x: position.x + (transform?.x ?? 0),
      y: position.y + (transform?.y ?? 0),
      scaleX: 1,
      scaleY: 1,
    }),
    cursor: isDragging ? "grabbing" : "grab",
    touchAction: "none", // prevents scroll-while-drag on touch devices
    willChange: "transform",
  }

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        if (containerRef) {
          ;(containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
      }}
      {...attributes}
      {...listeners}
      className={className}
      style={style}
      onPointerDownCapture={(e) => {
        pointerDownRef.current = { x: e.clientX, y: e.clientY }
        suppressClickRef.current = false
      }}
      onPointerUpCapture={(e) => {
        const start = pointerDownRef.current
        if (start) {
          const dx = e.clientX - start.x
          const dy = e.clientY - start.y
          const moved = Math.hypot(dx, dy)
          if (moved > 4) suppressClickRef.current = true
        }
      }}
      onClickCapture={(e) => {
        if (suppressClickRef.current) {
          e.preventDefault()
          e.stopPropagation()
          suppressClickRef.current = false
        }
      }}
    >
      {children}
    </div>
  )
}
